import React, { useEffect } from 'react';
import {
  Box,
  Button,
  Divider,
  useMediaQuery,
  useTheme,
} from '@ublend-npm/aulaui-core';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import MessageOutlinedIcon from '@mui/icons-material/MessageOutlined';
import ConversationCardSkeleton from './ConversationCardSkeleton';
import { toggleWidgetPreference } from '@app/actions/home/widgetPreferences';
import { useDispatch, useSelector } from 'react-redux';
import { WidgetCard, WidgetErrorState } from '../common';
import type { WidgetErrorType } from '../common/WidgetErrorState';
import ConversationListCard from '@app/components/presentational/MessagingResponsive/ConversationListCard';
import WidgetEmptyState from '../common/WidgetEmptyState';
import { NAVIGATION_SOURCE_MESSAGES } from '@core/constants/analytics';
import {
  trackHomeMessagesWidgetItemClicked,
  trackHomeViewAllClicked,
} from '../common';
import useTrackHomeWidgetLoaded from '../common/useTrackHomeWidgetLoaded';
import { saveHomeScrollPosition } from '../../homeScrollPosition';

interface MessageWidgetCardProps {
  handleConversationClick: (conversationId: string) => void;
  handleAllConversationButtonClick: () => void;
  conversations: any[];
  totalUnreadCount: number;
  mutedConversation?: Record<string, boolean | undefined>;
}

const ConversationListCardSkeletons: React.FC = () => {
  return (
    <>
      {Array.from({ length: 4 }).map((_, index) => (
        <Box key={index} mt={1}>
          <ConversationCardSkeleton />
        </Box>
      ))}
    </>
  );
};

const MessageWidgetCard: React.FC<MessageWidgetCardProps> = ({
  handleConversationClick,
  handleAllConversationButtonClick,
  conversations,
  totalUnreadCount,
  mutedConversation,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const limitedConversations = conversations.slice(0, 4);
  const [isLoading, setIsLoading] = React.useState(true);
  const fetchError = useSelector((state: any) => state.inboxv2.error);
  const errorType: WidgetErrorType | null = fetchError ? 'api' : null;
  const dispatch = useDispatch();

  const handleHideWidget = () => {
    dispatch(toggleWidgetPreference('home-messages-widget', true));
  };

  const menuActions = [{ label: 'Hide', onClick: handleHideWidget }];

  const fetchingConversations = useSelector(
    (state: any) => state.inboxv2.fetchingConversations
  );

  useEffect(() => {
    if (!fetchingConversations || errorType) {
      setIsLoading(false);
    }
  }, [fetchingConversations, errorType]);

  useTrackHomeWidgetLoaded(
    'messages',
    !isLoading && !errorType,
    conversations.length
  );

  const footer =
    conversations.length > 4 ? (
      <Button
        fullWidth
        kind="secondary"
        endIcon={<ArrowForwardIcon />}
        onClick={() => {
          if (isMobile) {
            saveHomeScrollPosition();
          }
          trackHomeViewAllClicked(
            NAVIGATION_SOURCE_MESSAGES,
            conversations.length
          );
          handleAllConversationButtonClick();
        }}
      >
        View all messages
      </Button>
    ) : undefined;

  const titleLeading = (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      width={40}
      height={40}
      mr={1}
      flexShrink={0}
      sx={{
        borderRadius: '100px',
        bgcolor: theme.palette.mode === 'dark' ? '#000000' : '#EAE8ED',
        color: 'primary.main',
      }}
    >
      <MessageOutlinedIcon sx={{ fontSize: 24 }} />
    </Box>
  );

  return (
    <WidgetCard
      title={'Messages'}
      titleLeading={titleLeading}
      badgeCount={totalUnreadCount}
      badgeMessage={`${totalUnreadCount} unread messages`}
      footer={footer}
      menuActions={menuActions}
      badgeBackgroundColor="#E8DCFC"
    >
      <Box>
        {isLoading ? (
          <ConversationListCardSkeletons />
        ) : errorType ? (
          <WidgetErrorState
            type={errorType}
            onRetry={() => window.location.reload()}
            isRetrying={isLoading}
          />
        ) : limitedConversations.length > 0 ? (
          limitedConversations.map((conversation, index) => (
            <Box key={conversation.id}>
              <ConversationListCard
                conversation={conversation}
                conversationMuted={!!mutedConversation?.[conversation.id]}
                selected={false}
                onClick={() => {
                  trackHomeMessagesWidgetItemClicked(
                    conversation.id,
                    NAVIGATION_SOURCE_MESSAGES,
                    index
                  );
                  handleConversationClick(conversation.id);
                }}
              />
              {index !== 3 && <Divider sx={{ marginBlock: 1 }} />}
            </Box>
          ))
        ) : (
          <Box sx={{ height: '488px' }}>
            <WidgetEmptyState
              emptyTitle="No messages yet"
              emptySubtitle="New messages will appear here when they're available."
            />
          </Box>
        )}
      </Box>
    </WidgetCard>
  );
};

export default MessageWidgetCard;
